import { buildResolveContext } from './buildResolveContext';

const isFilled = (v) =>
  v !== null &&
  v !== undefined &&
  !(typeof v === 'string' && v.trim() === '') &&
  !(Array.isArray(v) && v.length === 0);

const NOTE_TYPE_TO_SECTION = Object.freeze({
  Appointment: 'Appointments',
  Referral: 'Referrals',
  Consent: 'Consent',
  Sample: 'Samples',
  Test: 'Tests',
  Treatment: 'Treatments',
  'Tissue Pathology': 'Tissue pathology',
  'Clinical Note': 'Clinical notes',
  Process: 'Processes',
  Address: 'Addresses'
});

const PRIMARY_DATE_FIELDS = Object.freeze({
  Appointment: ['appointmentDate'],
  Referral: ['referralDate'],
  Consent: ['consentDate', 'consentRequestDate'],
  Sample: ['dateTaken', 'statusDate'],
  Test: ['dateRequested', 'dateResultReceived'],
  Treatment: ['treatmentDate', 'treatmentEndDate'],
  'Tissue Pathology': ['pathReportDate', 'reportReceivedDate'],
  'Clinical Note': ['clinicalNoteDate'],
  Process: ['processStartDate', 'endDate'],
  Address: []
});

const FALLBACK_DATE_FIELDS = ['createdAt', 'updatedAt'];

const COLLECTION_TO_SECTION = Object.freeze({
  Variants: 'Genotypes',
  Phenotypes: 'Phenotypes',
  Diagnoses: 'Diseases'
});

const COLLECTION_DATE_FIELDS = Object.freeze({
  Variants: ['dateRecorded'],
  Phenotypes: ['dateRecorded'],
  Diagnoses: ['dateReported']
});

/**
 * Pick the "primary" (most recent) record from a homogeneous list of clinical
 * records. First filled date field in `candidateFields` (then the universal
 * `createdAt`/`updatedAt`) drives the sort; the newest wins.
 *
 * @param {Array<object>} records
 * @param {string[]}     [candidateFields=[]]
 * @returns {object|null}
 */
export function pickPrimaryRecord(records, candidateFields = []) {
  if (!Array.isArray(records) || records.length === 0) return null;
  const fields = [...candidateFields, ...FALLBACK_DATE_FIELDS];
  const scored = records
    .map((r) => {
      let iso = null;
      for (const f of fields) {
        if (r && r[f]) {
          iso = r[f];
          break;
        }
      }
      const ts = iso ? new Date(iso).getTime() : 0;
      return { record: r, ts: Number.isFinite(ts) ? ts : 0 };
    })
    .sort((a, b) => b.ts - a.ts);
  return scored[0].record;
}

/**
 * Back-compat shim — was the only export before the fix generalised to every
 * clinical-record type. Kept so existing call sites (and their tests) keep
 * working; internally it delegates to the generic picker.
 *
 * @param {Array<object>} memberTests
 * @returns {object|null}
 */
export function pickPrimaryTest(memberTests) {
  return pickPrimaryRecord(memberTests, PRIMARY_DATE_FIELDS.Test);
}

function formatDisplay(v) {
  if (!isFilled(v)) return '';
  if (v instanceof Date) return v.toLocaleDateString();
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.filter(isFilled).map(formatDisplay).join(', ');
  if (typeof v === 'object') {
    // Structured date shape from buildResolveContext ({ iso, precision, ... })
    if (v.iso) return formatDisplay(v.iso);
    return '';
  }
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString();
  }
  return s;
}

/**
 * Extend a person-level resolve context with clinical-record fields sourced
 * from the picked primary record for each noteType we know about. The copy
 * is GENERIC — every own-property of the primary record lands at
 * `<Section>.<key>` — so schema growth in `import-schema.json` requires no
 * change here; only new noteType → section entries need adding above.
 *
 * Non-plain-object fields (arrays, nested docs like `staffMember`,
 * `fileAttachments`, `variants`) are copied verbatim; `formatDisplay`
 * decides later whether they render into the letter or fall through as
 * "unresolved". This is intentional: displaying an array of file objects
 * isn't useful, so the letter author sees the `{{placeholder}}` and knows
 * to pick a scalar field instead.
 *
 * @param {object} values                    accumulator, mutated + returned
 * @param {Record<string, Array<object>>} memberRecordsByType   noteType → records
 */
function extendContextWithClinicalRecords(values, memberRecordsByType) {
  if (!memberRecordsByType) return values;
  for (const [noteType, section] of Object.entries(NOTE_TYPE_TO_SECTION)) {
    const records = memberRecordsByType[noteType];
    if (!Array.isArray(records) || !records.length) continue;
    const primary = pickPrimaryRecord(records, PRIMARY_DATE_FIELDS[noteType]);
    if (!primary) continue;
    for (const [k, v] of Object.entries(primary)) {
      if (k.startsWith('_')) continue; // skip _id / __v internal keys
      values[`${section}.${k}`] = v;
    }
  }
  return values;
}

function extendContextWithCollections(values, collections) {
  if (!collections) return values;
  for (const [collectionToken, section] of Object.entries(
    COLLECTION_TO_SECTION
  )) {
    const items = collections[collectionToken];
    if (!Array.isArray(items) || !items.length) continue;
    const primary = pickPrimaryRecord(
      items,
      COLLECTION_DATE_FIELDS[collectionToken]
    );
    if (!primary) continue;
    for (const [k, v] of Object.entries(primary)) {
      values[`${section}.${k}`] = v;
    }
  }
  return values;
}

export function flattenRecordIntoSection(values, section, record) {
  if (!record) return values;
  const next = { ...values };
  for (const [k, v] of Object.entries(record)) {
    if (k.startsWith('_')) continue;
    next[`${section}.${k}`] = v;
  }
  return next;
}

export function buildFullResolveContext(opts = {}) {
  const {
    record = {},
    contactFormValues = {},
    staffNames = [],
    memberRecordsByType,
    memberTests
  } = opts;

  const grouped = { ...(memberRecordsByType || {}) };
  if (!grouped.Test && Array.isArray(memberTests)) grouped.Test = memberTests;

  const ctx = buildResolveContext(record, contactFormValues, staffNames);
  let values = extendContextWithClinicalRecords({ ...ctx.values }, grouped);
  values = extendContextWithCollections(values, ctx.collections);

  const noteTypeCollections = {};
  for (const [noteType, token] of Object.entries(NOTE_TYPE_TO_SECTION)) {
    noteTypeCollections[token] = grouped[noteType] || [];
  }
  const collections = { ...ctx.collections, ...noteTypeCollections };

  return { values, collections };
}

export function buildCataloguePreviewDefaults(catalogueGroups, opts = {}) {
  if (!Array.isArray(catalogueGroups) || !catalogueGroups.length) return {};
  const { values } = buildFullResolveContext(opts);

  const out = {};
  for (const group of catalogueGroups) {
    const fields = (group && group.fields) || [];
    for (const f of fields) {
      if (!f || !f.token || !f.path) continue;
      if (f.kind === 'collection') continue;
      const raw = values[f.path];
      if (!isFilled(raw)) continue;
      const display = formatDisplay(raw);
      if (display === '') continue;
      out[f.token] = display;
    }
  }
  return out;
}

export { NOTE_TYPE_TO_SECTION, PRIMARY_DATE_FIELDS };
